import { useCallback, useState } from "react";

import UserService from "@/services/user.service";

import { isTerminalPaymentFailure, resolvePurchasePaymentStep } from "./purchasePayment.utils";

const CONFIRMATION_ATTEMPTS = 12;
const CONFIRMATION_INTERVAL_MS = 500;

function wait(delay) {
  return new Promise((resolve) => setTimeout(resolve, delay));
}

export default function useArtworkPurchasePayment({ navigate, resolveStripeInstance }) {
  const [purchasePayment, setPurchasePayment] = useState(null);

  const waitForOrderConfirmation = useCallback(async (paymentIntentId) => {
    for (let attempt = 0; attempt < CONFIRMATION_ATTEMPTS; attempt += 1) {
      const { response, data } = await UserService.getArtworkPaymentIntentStatus(paymentIntentId);
      if (!response?.ok) {
        throw new Error(data?.error_message ?? "Unable to confirm the order payment.");
      }

      if (data?.order_status === "paid") {
        return data;
      }

      if (data?.error_type) {
        const error = new Error(data?.error_message ?? "The artwork is no longer available.");
        error.code = data.error_type;
        throw error;
      }

      if (isTerminalPaymentFailure(data?.payment_intent_status)) {
        throw new Error("The payment was not completed.");
      }

      if (attempt < CONFIRMATION_ATTEMPTS - 1) {
        await wait(CONFIRMATION_INTERVAL_MS);
      }
    }

    throw new Error("Your payment is still being confirmed. Please check your orders shortly.");
  }, []);

  const completePurchasePayment = useCallback(async (paymentIntent) => {
    const paymentIntentId = paymentIntent?.id;
    if (!paymentIntentId) {
      throw new Error("The purchase payment intent is missing.");
    }

    await waitForOrderConfirmation(paymentIntentId);
    setPurchasePayment(null);
    navigate("/orders/confirmed", { replace: true });
  }, [navigate, waitForOrderConfirmation]);

  const handlePaymentIntentResponse = useCallback(async (paymentResponse) => {
    const nextStep = resolvePurchasePaymentStep(paymentResponse);

    if (nextStep.type === "collect_payment_method") {
      const stripe = await resolveStripeInstance();
      if (!stripe) {
        throw new Error("Unable to load the secure payment form. Please try again.");
      }
      setPurchasePayment({ clientSecret: nextStep.clientSecret, stripe });
      return;
    }

    if (nextStep.type === "confirm_saved_payment_method") {
      const stripe = await resolveStripeInstance();
      if (!stripe) {
        throw new Error("Unable to load the secure payment form. Please try again.");
      }
      const result = await stripe.confirmCardPayment(nextStep.clientSecret);
      if (result?.error) {
        throw result.error;
      }
      await completePurchasePayment(result?.paymentIntent ?? { id: nextStep.paymentIntentId });
      return;
    }

    await completePurchasePayment({ id: nextStep.paymentIntentId });
  }, [completePurchasePayment, resolveStripeInstance]);

  const dismissPurchasePayment = useCallback(() => {
    setPurchasePayment(null);
  }, []);

  return {
    completePurchasePayment,
    dismissPurchasePayment,
    handlePaymentIntentResponse,
    purchasePayment,
  };
}
